import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useNotificationStore } from '../../store/notificationStore';
import { completeXamanLogin, linkWallet, getLinkedWallets } from '../../api/auth';
import { WalletConnectModal } from './WalletConnectModal';
import { WALLET_BRANDS } from '../../lib/wallet';
import { getReferralCode, clearReferralCode } from '../../utils/referral';

/**
 * XamanModal - Authentication-only modal for Xaman wallet connection
 *
 * ⚠️ IMPORTANT: This component is ONLY for authentication flows (login/link wallet).
 * DO NOT use this for transaction signing (sell, buy, cancel, transfer, make offer, etc.)
 *
 * USE CASES:
 * - User login via Xaman wallet signature
 * - Linking additional wallets to an existing account
 *
 * HOW IT WORKS:
 * - Polls `/auth/xaman/verify` endpoint for login completion
 * - Polls `/auth/link-wallet` endpoint for wallet linking
 * - Shows success notification and calls onSuccess when complete
 *
 * FOR TRANSACTION SIGNING, USE INSTEAD:
 * - XamanSigningModal: For simple transaction signing (sell, cancel, transfer, make offer)
 * - XamanTransactionModal: For buy flows that need a confirmation step before signing
 *
 * @see XamanSigningModal - For transaction signing
 * @see XamanTransactionModal - For buy flows with confirmation
 */
interface XamanModalProps {
  uuid: string;
  qrCode: string;
  deepLink: string;
  onClose: () => void;
  onSuccess: () => void;
  mode?: 'login' | 'link'; // 'login' for normal login, 'link' for adding wallet to existing account
}

export const XamanModal: React.FC<XamanModalProps> = ({
  uuid,
  qrCode,
  deepLink,
  onClose,
  onSuccess,
  mode = 'login',
}) => {
  const { user, setUser, addLinkedWallet, setLinkedWallets, storeWalletToken, switchToWallet } = useAuthStore();
  const { success: showSuccess, error: showError } = useNotificationStore();
  const location = useLocation();
  const [polling, setPolling] = useState(true);

  // Guard against duplicate success callbacks
  const successCalledRef = useRef(false);

  console.log(`[XamanModal] Mounted with mode: ${mode}, uuid: ${uuid}`);

  useEffect(() => {
    let interval: NodeJS.Timeout;

    const pollLogin = async () => {
      // Guard: If success was already called, skip
      if (successCalledRef.current) {
        console.log('[XamanModal] pollLogin: SKIPPING - success already called');
        return;
      }

      try {
        // Get referral code from cookie if present
        const referralCode = getReferralCode() || undefined;

        console.log(`[XamanModal] pollLogin: Polling for uuid: ${uuid}`);
        const data = await completeXamanLogin(uuid, referralCode);
        if (data.user && data.token) {
          // Guard against duplicate callbacks
          if (successCalledRef.current) {
            console.log('[XamanModal] pollLogin: SKIPPING duplicate success callback');
            return;
          }
          successCalledRef.current = true;
          console.log('[XamanModal] pollLogin: Login successful, calling setUser and showing success');

          setUser(data.user, data.token, 'xaman');
          showSuccess('Successfully connected with Xaman!');
          setPolling(false);

          // Clear the referral cookie after successful login
          clearReferralCode();

          // Fetch linked wallets for the new user
          try {
            const wallets = await getLinkedWallets();
            setLinkedWallets(wallets);
          } catch (err) {
            console.error('Failed to fetch linked wallets after login:', err);
          }

          // If on home page, trigger a refresh to show logged-in view
          if (location.pathname === '/') {
            // Close modal first, then refresh
            onSuccess();
            // Give a small delay for the success message to show, then refresh
            setTimeout(() => {
              window.location.reload();
            }, 500);
          } else {
            onSuccess();
          }
        }
      } catch (err: any) {
        // Silently continue polling unless it's a real error
        if (err.message && !err.message.includes('not completed')) {
          console.error('Polling error:', err);
        }
      }
    };

    const pollLink = async () => {
      // Guard: If success was already called, skip
      if (successCalledRef.current) {
        console.log('[XamanModal] pollLink: SKIPPING - success already called');
        return;
      }

      try {
        console.log(`[XamanModal] pollLink: Polling for uuid: ${uuid}`);
        const data = await linkWallet(uuid);

        if (data.wallet && data.token) {
          // Guard against duplicate callbacks
          if (successCalledRef.current) {
            console.log('[XamanModal] pollLink: SKIPPING duplicate success callback');
            return;
          }
          successCalledRef.current = true;
          console.log('[XamanModal] pollLink: Link successful');
          const newUser = {
            ...user!,
            xrpl_address: data.wallet.xrpl_address,
          };

          // Store the new wallet and its token in zustand (this persists to localStorage)
          addLinkedWallet(data.wallet, data.token, newUser);

          // Switch to the newly linked wallet using the store method
          // This updates zustand state AND localStorage properly
          storeWalletToken(data.wallet.xrpl_address, data.token, newUser);
          switchToWallet(data.wallet.xrpl_address);

          showSuccess(`Wallet ${data.wallet.xrpl_address.slice(0, 8)}... linked and switched!`);
          setPolling(false);
          onSuccess();

          // Small delay to ensure zustand persist middleware flushes to localStorage
          setTimeout(() => {
            window.location.reload();
          }, 100);
        }
      } catch (err: any) {
        const errorMessage = err.message || '';

        // Still waiting for signature, continue polling
        if (errorMessage.includes('not completed') || errorMessage.includes('Authentication not completed')) {
          return;
        }

        // Stop polling for any real error
        setPolling(false);

        // Show user-friendly error messages
        if (errorMessage.includes('already linked to another') || errorMessage.includes('another account')) {
          showError('This wallet is already linked to another account.');
        } else if (errorMessage.includes('already linked to your') || errorMessage.includes('your account')) {
          showError('This wallet is already linked to your account.');
        } else if (errorMessage) {
          // Show the actual error message for other errors
          showError(errorMessage);
        } else {
          showError('Failed to link wallet. Please try again.');
        }

        console.error('Link wallet error:', err);
        onClose();
      }
    };

    const poll = mode === 'link' ? pollLink : pollLogin;

    if (polling) {
      console.log(`[XamanModal] Starting polling interval for mode: ${mode}`);
      interval = setInterval(poll, 2000);
    }

    return () => {
      console.log(`[XamanModal] Cleanup: clearing polling interval`);
      if (interval) clearInterval(interval);
    };
  }, [uuid, polling, setUser, addLinkedWallet, setLinkedWallets, user, showSuccess, showError, onSuccess, onClose, location, mode]);

  const title = mode === 'link' ? 'Link Another Wallet' : 'Connect with Xaman';
  const subtitle = mode === 'link'
    ? 'Sign to link this wallet to your account'
    : 'Scan the QR code or tap the button below';

  return (
    <WalletConnectModal
      open
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      wallet={WALLET_BRANDS.xaman}
      qrUrl={qrCode}
      deepLink={deepLink}
      statusText="Waiting for signature…"
      statusVariant="waiting"
    />
  );
};
